import {
  ActionButtons,
  Badge,
  DataTable,
  type DataTableColumn,
  type DataTableExpandedTableConfig,
  type DataTableRow,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import ApplyAdjustmentModal from '@components/layout/Modals/ApplyAdjustmentModal/ApplyAdjustmentModal.component'
import ViewAppointmentInfo from '@components/layout/Modals/ViewAppointmentInfo/ViewAppointmentInfo.component'
import { BALANCE_BADGE_COLOR } from '@constants/Billing'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import { BalanceBadgeStatus, LedgerEntry } from '@dataTypes/billing.types'
import { formatCurrency, formatInvoiceNumber } from '@utils/billing.utils'
import React, { useCallback, useMemo, useState } from 'react'
import type { LedgerTableHooks } from './useLedger'
import './LedgerTableStyle.css'

type Props = LedgerTableHooks

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const DEFAULT_SORT_COLUMN_ID = 'appointmentDate'
const DEFAULT_SORT_DIRECTION: DataTableSortDirection = 'desc'

const EXPANDED_COLUMNS: DataTableColumn[] = [
  { id: 'clinicFee', label: 'Clinic Fee' },
  { id: 'allowedAmount', label: 'Allowed amt.' },
  { id: 'writeOff', label: 'Write-off' },
  { id: 'estimatedInsurance', label: 'Estimated ins.' },
  { id: 'discount', label: 'Discount' },
  { id: 'appointmentAdjustment', label: 'Appointment adj.' },
  { id: 'spacer', label: '', renderCell: () => '' },
]

const sumProcedureCash = (
  procedures: LedgerEntry['procedures'],
  cashKey: 'insuranceCash' | 'patientCash',
  adjustmentKey: 'insuranceAdjustment' | 'patientAdjustment',
): number =>
  (procedures || []).reduce(
    (sum, procedure) =>
      sum + (procedure[cashKey] || 0) + (procedure[adjustmentKey] || 0),
    0,
  )

const resolveBalanceStatus = (
  balance: number,
  cashAndAdjustment: number,
): BalanceBadgeStatus => {
  if (cashAndAdjustment > 0 && balance !== 0) {
    return BalanceBadgeStatus.Partial
  }
  if (balance === 0 && cashAndAdjustment > 0) {
    return BalanceBadgeStatus.Paid
  }
  return BalanceBadgeStatus.Unpaid
}

const LedgerTable: React.FC<Props> = ({
  handleView,
  filteredLedger,
  ledgerCount,
  currentPage,
  pageSize,
  handlePageChange,
  handlePageSizeChange,
  handleTableSortChange,
  selectedLedger,
  showModal,
  handleModalClose,
  relatedLedgerEntries,
  handleCreateInvoiceFromLedger,
}): JSX.Element => {
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false)
  const [selectedLedgerForAdjustment, setSelectedLedgerForAdjustment] =
    useState<LedgerEntry | null>(null)
  const [sortColumnId, setSortColumnId] = useState(DEFAULT_SORT_COLUMN_ID)
  const [sortDirection, setSortDirection] = useState<DataTableSortDirection>(
    DEFAULT_SORT_DIRECTION,
  )
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([])

  const entriesById = useMemo(() => {
    const entries = new Map<string, LedgerEntry>()
    filteredLedger.forEach((entry) => {
      entries.set(entry.appointmentId, entry)
    })
    return entries
  }, [filteredLedger])

  const rows = useMemo(
    (): DataTableRow[] =>
      filteredLedger.map((entry) => ({
        id: entry.appointmentId,
        patientName: entry.patientName,
        providerName: entry.providerName,
        appointmentDate: entry.appointmentDate,
      })),
    [filteredLedger],
  )

  const handleAdjustmentClick = (row: LedgerEntry): void => {
    setSelectedLedgerForAdjustment(row)
    setShowAdjustmentModal(true)
  }

  const handleCloseAdjustmentModal = (): void => {
    setShowAdjustmentModal(false)
    setSelectedLedgerForAdjustment(null)
  }

  const handleCreateInvoiceClick = (row: LedgerEntry): void => {
    if (!row.invoiceNumber) {
      handleCreateInvoiceFromLedger(row)
    }
  }

  const handleSort = useCallback(
    (columnId: string): void => {
      const nextDirection: DataTableSortDirection =
        sortColumnId === columnId && sortDirection === 'asc' ? 'desc' : 'asc'
      setSortColumnId(columnId)
      setSortDirection(nextDirection)
      handleTableSortChange({
        key: columnId,
        direction:
          nextDirection === 'asc' ? SortDirection.ASC : SortDirection.DESC,
      })
    },
    [handleTableSortChange, sortColumnId, sortDirection],
  )

  const columns = useMemo((): DataTableColumn[] => {
    const renderBalanceCell = (
      entry: LedgerEntry,
      amount: number | null,
      balance: number,
      cashKey: 'insuranceCash' | 'patientCash',
      adjustmentKey: 'insuranceAdjustment' | 'patientAdjustment',
    ): React.ReactNode => (
      <span className="insurance-amount-cell">
        {formatCurrency(amount)}{' '}
        <Badge
          mode="tinted"
          color={
            BALANCE_BADGE_COLOR[
              resolveBalanceStatus(
                balance,
                sumProcedureCash(entry.procedures, cashKey, adjustmentKey),
              )
            ]
          }
          label={formatCurrency(balance)}
        />
      </span>
    )

    return [
      {
        id: 'patientName',
        label: 'Patient',
        sortable: true,
        width: '133px',
      },
      {
        id: 'invoiceNumber',
        label: 'Invoice no.',
        width: '140px',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          return entry ? formatInvoiceNumber(entry.invoiceNumber) : null
        },
      },
      {
        id: 'providerName',
        label: 'Provider name',
        sortable: true,
        width: '133px',
      },
      {
        id: 'appointmentDate',
        label: 'Appt. date',
        sortable: true,
        width: '133px',
      },
      {
        id: 'approvedInsurance',
        label: 'Approved ins.',
        width: '133px',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          if (!entry) return null
          return renderBalanceCell(
            entry,
            entry.approvedInsurance,
            entry.insuranceBalance || 0,
            'insuranceCash',
            'insuranceAdjustment',
          )
        },
      },
      {
        id: 'patientResponsibility',
        label: 'Patient resp.',
        width: '133px',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          if (!entry) return null
          return renderBalanceCell(
            entry,
            entry.patientResponsibility,
            entry.patientBalance || 0,
            'patientCash',
            'patientAdjustment',
          )
        },
      },
      {
        id: 'appointmentBalance',
        label: 'Appointment balance',
        width: '133px',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          return entry ? formatCurrency(entry.appointmentBalance) : null
        },
      },
    ]
  }, [entriesById])

  const expandedTable = useMemo(
    (): DataTableExpandedTableConfig => ({
      columns: EXPANDED_COLUMNS,
      getRows: (parentRow) => {
        const entry = entriesById.get(parentRow.id)
        if (!entry) return []
        return [
          {
            id: `${entry.appointmentId}-summary`,
            clinicFee: formatCurrency(entry.clinicFee),
            allowedAmount: formatCurrency(entry.allowedAmount),
            writeOff: formatCurrency(entry.writeOff),
            estimatedInsurance: formatCurrency(entry.estimatedInsurance),
            discount: formatCurrency(entry.discount),
            appointmentAdjustment: formatCurrency(entry.appointmentAdjustment),
          },
        ]
      },
    }),
    [entriesById],
  )

  const renderActions = (row: DataTableRow): React.ReactNode => {
    const entry = entriesById.get(row.id)
    if (!entry) return null

    return (
      <ActionButtons
        onView={() => handleView(entry)}
        onRefund={() => handleAdjustmentClick(entry)}
        disableRefund={!entry.invoiceNumber}
        onCreateInvoice={() => handleCreateInvoiceClick(entry)}
        disableCreateInvoice={!!entry.invoiceNumber}
      />
    )
  }

  const totalPages = Math.max(1, Math.ceil(ledgerCount / pageSize))

  return (
    <>
      <DataTable
        className="ledger-table"
        columns={columns}
        rows={rows}
        expandable
        expandableColumnId="patientName"
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={setExpandedRowIds}
        expandedTable={expandedTable}
        showActions
        actionsLabel="Actions"
        renderActions={renderActions}
        sortColumnId={sortColumnId}
        sortDirection={sortDirection}
        onSort={handleSort}
        pagination={{
          currentPage,
          totalPages,
          pageSize,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
      />

      {selectedLedger && (
        <ViewAppointmentInfo
          isVisible={showModal}
          closeModal={handleModalClose}
          ledgerEntries={relatedLedgerEntries}
          initialAppointmentId={selectedLedger.appointmentId}
        />
      )}

      <ApplyAdjustmentModal
        isVisible={showAdjustmentModal}
        closeModal={handleCloseAdjustmentModal}
        ledgerEntry={selectedLedgerForAdjustment}
      />
    </>
  )
}

export default LedgerTable
